/**
 * Offline-Tutorial
 *
 * Erklärt, was ohne Internetverbindung funktioniert und was nicht.
 *
 * Abgedeckte Prozesse: P9 (Offline-Erfassung und Sync), P26 (Bedienung ohne
 * Verbindung) — siehe docs/PROZESSLANDKARTE.md.
 *
 * Besonderheit: Die entscheidenden Anzeigen — Hinweisstreifen, Sync-Symbol,
 * Pending-Markierung — existieren nur, WÄHREND keine Verbindung besteht. Sie
 * lassen sich also nicht hervorheben, solange der Trainer online ist, und ein
 * Element-Schritt darauf fiele still auf ein zentriertes Fenster zurück. Deshalb
 * bekommen nur die dauerhaft vorhandenen Bedienelemente einen Anker; der Rest
 * wird beschrieben.
 */

import type { TutorialDefinition, TutorialStep } from './types'
import { useCoursesStore } from '@/stores/courses.store'
import { useSessionsStore } from '@/stores/sessions.store'

function createSteps(): TutorialStep[] {
  const coursesStore = useCoursesStore()
  const sessionsStore = useSessionsStore()

  const firstCourse = coursesStore.courses[0]
  const session = firstCourse ? sessionsStore.getMostRelevantSession(firstCourse.id) : undefined

  const steps: TutorialStep[] = [
    {
      popover: {
        title: 'Arbeiten ohne Internet',
        description:
          'In vielen Sporthallen gibt es kein Netz. Die App ist dafür gebaut — diese Anleitung zeigt dir, was trotzdem geht und was warten muss.',
      },
    },
  ]

  if (firstCourse && session) {
    steps.push(
      {
        navigateTo: `/course/${firstCourse.id}/session/${session.id}/attendance`,
        element: '[data-tutorial="attendance-list"]',
        popover: {
          title: 'Anwesenheit geht immer',
          description:
            'Die Anwesenheitsliste funktioniert ohne Verbindung genauso wie mit. Deine Eintragungen speichert die App auf dem Gerät und überträgt sie später von selbst.',
          position: 'top',
        },
      },
      {
        element: '[data-tutorial="guest-section"]',
        popover: {
          title: 'Gäste auch',
          description:
            'Neue Gäste kannst du ebenfalls ohne Verbindung anlegen. Entfernen lassen sie sich dagegen erst wieder, wenn du online bist.',
          position: 'top',
        },
      }
    )
  }

  steps.push(
    {
      popover: {
        title: 'Woran du es erkennst',
        description:
          'Ohne Verbindung erscheint unten ein Hinweisstreifen. Eintragungen, die noch übertragen werden müssen, bekommen einen kleinen Punkt; darüber steht, wie viele es sind.',
      },
    },
    {
      popover: {
        title: 'Die Übertragung läuft von selbst',
        description:
          'Sobald wieder Netz da ist, überträgt die App alles automatisch und meldet sich kurz. Du musst nichts anstoßen. Schlägt etwas fehl, kannst du es im Hinweis erneut versuchen oder verwerfen.',
      },
    },
    {
      popover: {
        title: 'Was warten muss',
        description:
          'Nur online möglich sind: Mitglieder hinzufügen oder entfernen, Gäste löschen und die gesamte Abrechnung. Die Aktualisieren-Schaltflächen sind dann ausgegraut.',
      },
    }
  )

  return steps
}

export const offlineTutorial: TutorialDefinition = {
  id: 'offline',
  title: 'Arbeiten ohne Internet',
  description: 'Was in der Halle ohne Netz funktioniert und was auf später wartet.',
  icon: 'pi pi-wifi',
  steps: createSteps,
}
